import {
  Component,
  Inject,
  InjectionToken,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { getAdvancedMode, State } from 'search/reducers/root.reducer';
import {
  Subject,
  Subscription,
} from 'rxjs';
import {
  ResourceFacetSelectedAction,
  HandleFacetsLockInStorage,
} from 'search/actions/facet.actions';
import { defaultDateFacets } from 'search/models/search-object';
import { YearDirective } from 'common/directives/year.directive';
import { copyObject } from 'search/reducers/utils';
import { FacetState } from 'search/facets/models/facet-state';
import { DateFacet } from 'search/facets/models/resource-facet';
import { debounceTime } from 'rxjs/operators';
import { ComponentData } from 'search/models/filter-panel';

export const DATE_FACET_COMPONENT_DATA = new InjectionToken<{}>('DATE_FACET_COMPONENT_DATA');

@Component({
  selector: 'app-date-facet',
  templateUrl: './date-facet.component.html',
  styleUrls: ['./date-facet.component.scss'],
  providers: [YearDirective],
})
export class DateFacetComponent implements OnInit, OnDestroy {

  public set checked(checked: boolean) {
    const facet = {
      ...this.getDateFacets(),
      selected: checked,
    };
    this.store.dispatch(new ResourceFacetSelectedAction(facet));
    if (checked && this.isAdvancedMode && !facet.isResetLocked) {
      this.store.dispatch(new HandleFacetsLockInStorage({facet}));
    }
    this._checked = checked;
  }

  public dateFrom = '';
  public dateTo = '';
  public isLoading: Subject<boolean> = new Subject().pipe(debounceTime(300)) as Subject<boolean>;
  public isResetLocked = false;
  public expanded: boolean;
  public facetKey: string;
  public facetBlockName: string;
  public _checked: boolean;

  private isAdvancedMode: boolean = false;
  private subscriptions = new Subscription();

  private readonly DATE_REGEXP = /^\d{1,4}$/;

  constructor(
    private store: Store<State>,
    @Inject(DATE_FACET_COMPONENT_DATA) public componentData: ComponentData,
  ) { }

  public ngOnInit() {
    this.subscriptions.add(this.componentData.facetData
      .subscribe((data: FacetState<DateFacet>) => {
        const selectedDateFacet = data.selected;
        this.facetBlockName = data.schemaWithData.schema.blockName;
        this._checked = selectedDateFacet.selected;
        this.dateFrom = selectedDateFacet.dateFrom;
        this.dateTo = selectedDateFacet.dateTo;
        this.isLoading.next(data.isLoading);
        this.isResetLocked = selectedDateFacet.isResetLocked;
        this.expanded = data.expanded;
      }));
    this.subscriptions.add(this.store.select(getAdvancedMode).subscribe((advanced) => {
      this.isAdvancedMode = advanced;
    }));
    this.facetKey = this.componentData.facetKey;
  }

  public onChange(checked: boolean) {
    this.checked = checked;
  }

  public onDateFromYearChange(value: any) {
    this.dateFrom = value;
    this.handleYearChange();
  }

  public onDateToYearChange(value: any) {
    this.dateTo = value;
    this.handleYearChange();
  }

  public onDateFromClear() {
    this.dateFrom = '';
    this.checked = !this.dateFrom && !this.dateTo ? false : this._checked;
  }

  public onDateToClear() {
    this.dateTo = '';
    this.checked = !this.dateFrom && !this.dateTo ? false : this._checked;
  }

  public onToggleBubbleLock() {
    this.store.dispatch(new HandleFacetsLockInStorage({ facet: this.getDateFacets() }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private handleYearChange() {
    const fromIsNotSetOrFull = (!this.dateFrom || (this.DATE_REGEXP.test(this.dateFrom.toString())));
    const toIsNotSetOrFull = (!this.dateTo || (this.DATE_REGEXP.test(this.dateTo.toString())));
    const fromOrToDateExists = !!(this.dateFrom || this.dateTo);
    this.checked = fromIsNotSetOrFull && toIsNotSetOrFull && fromOrToDateExists;
  }

  private getDateFacets() {
    const dateFacets = copyObject(defaultDateFacets);
    if (this.dateFrom) {
      dateFacets.dateFrom = this.dateFrom;
    }
    if (this.dateTo) {
      dateFacets.dateTo = this.dateTo;
    }
    dateFacets.isResetLocked = this.isResetLocked;

    dateFacets.label = `${dateFacets.dateFrom}${dateFacets.dateFrom && dateFacets.dateTo ? '—' : ''}${dateFacets.dateTo}`;
    dateFacets.id = dateFacets.label;

    return dateFacets;
  }

}
